/* React */
import React, { useEffect } from "react";

/* React Router */
import { useLocation } from "react-router-dom";

/* React Redux */
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/redux/store";

import {
  setInsightsFilterValue,
  disableFilter,
  clearFilterRestriction,
  initCareSites,
} from "src/redux/features/insights-filter/insightsFilterSlice";

/* Thunks */
import { fetchInsightsFilter } from "src/redux/thunks/insights-filter/insightsFilterThunks";

/* Config */
import { availableInsightsFilter } from "src/config/filter";

/* Utils */
import { useResetOnFilterChange } from "src/utils/hooks/insights-filter/useResetOnFilterChange";
import { renderFilterComponent } from "src/components/insights/insights-filter/renderFilterComponent";

/* Interfaces */
import {
  FilterType,
  TimePeriodProps,
} from "src/shared/interfaces/insights-filter/InsightsFilter";

const InsightsFilter: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  useResetOnFilterChange();

  const insightsFilterState = useSelector(
    (state: RootState) => state.insightsFilter
  );

  const allowedCareSites = useSelector(
    (state: RootState) => state.auth.allowedCareSites
  );
  const allowedCareSitesStatus = useSelector(
    (state: RootState) => state.auth.allowedCareSitesStatus
  );

  useEffect(() => {
    if (allowedCareSites === null) {
      // Skip during a transient loading state to preserve the user's narrowed
      // selection. Clear only on a true logout/reset (idle) or error.
      if (allowedCareSitesStatus !== "loading") {
        dispatch(initCareSites([]));
      }
      return;
    }
    dispatch(initCareSites(allowedCareSites));
  }, [dispatch, allowedCareSites, allowedCareSitesStatus]);

  const { mainCondition } = insightsFilterState;

  const customCohortsState = useSelector(
    (state: RootState) => state.customCohorts
  );

  const { currentDiseaseId, customCohortsByDisease } =
    customCohortsState;

  const handleValue = (
    key: FilterType,
    value?: string | TimePeriodProps
  ) => {
    dispatch(setInsightsFilterValue({ key, value }));
  };

  useEffect(() => {
    if (!mainCondition?.value) return;

    dispatch(fetchInsightsFilter(mainCondition.value));
  }, [dispatch, mainCondition?.value]);

  useEffect(() => {
    if (!currentDiseaseId) return;

    if (
      customCohortsByDisease[currentDiseaseId].cohorts
        .length > 0
    ) {
      dispatch(disableFilter(FilterType.COHORTS));
    } else {
      dispatch(clearFilterRestriction(FilterType.COHORTS));
    }
  }, [dispatch, customCohortsByDisease, currentDiseaseId]);

  const currentPath = location.pathname;
  const orderedFilterKeys: FilterType[] =
    availableInsightsFilter[currentPath] ?? [];

  return (
    <>
      {orderedFilterKeys.map((key) => {
        const item = insightsFilterState[key];

        if (!item || !item.show) return null;

        return renderFilterComponent({
          itemKey: key,
          item,
          handleValue,
          insightsFilter: insightsFilterState,
        });
      })}
    </>
  );
};

export default InsightsFilter;
